import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../FlashMessage';
import SearchableSelect from '../Invoicing/SearchableSelect';
import stockService from '../../services/stockService';

/**
 * Service Quick Creator (HMS request — create services from the invoice
 * line-item "create" modal, not just products).
 *
 * A single-step sibling to StockItemForm / StockItemVariantForm, rendered by
 * InlineProductCreator when the user chooses "Service" instead of "Product".
 * It creates a stock.Service master record (reusable; surfaces in the Stock
 * module's Services tab) and hands the created service back via onSuccess so
 * the calling line item can be filled in (description + default GL account).
 *
 * It deliberately does NOT collect price / quantity: a Service is master data
 * with no pricing. The operator enters quantity / unit price / VAT on the
 * invoice line itself, exactly as they would for a free-text line.
 */
const ServiceQuickForm = ({
    isOpen,
    onClose,
    onSuccess,
    initialDescription = '',
    initialServiceType = '',
}) => {
    const { showSuccess } = useFlashMessage();

    const [serviceTypes, setServiceTypes] = useState([]);
    const [formData, setFormData] = useState({
        service_type: initialServiceType || '',
        description: initialDescription || '',
        gl_account: '',
        cost_category: '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const types = await stockService.fetchServiceTypes();
                if (!cancelled) setServiceTypes(types || []);
            } catch (err) {
                console.error('Failed to load service types:', err);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Re-seed description / service type when the pre-fill changes (e.g. the
    // user typed a different search term before opening the creator).
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            description: initialDescription || prev.description,
            service_type: initialServiceType || prev.service_type,
        }));
    }, [initialDescription, initialServiceType]);

    const handleAccountingChange = (name) => (id) => {
        setFormData((prev) => ({ ...prev, [name]: id || '' }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.service_type) newErrors.service_type = 'Service type is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const payload = {
                service_type: formData.service_type,
                description: formData.description.trim(),
                gl_account: formData.gl_account || null,
                cost_category: formData.cost_category || null,
            };
            const created = await stockService.createService(payload);
            showSuccess('Service created successfully');
            onSuccess(created);
        } catch (error) {
            console.error('Error creating service:', error);
            if (error.response?.status === 400 && error.response.data && typeof error.response.data === 'object') {
                setErrors(error.response.data);
            } else if (error.response?.status === 401) {
                setErrors({ general: 'You are not authorized to create services. Please log in again.' });
            } else {
                setErrors({ general: 'Failed to create service. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content stock-form-modal">
                <div className="modal-header">
                    <h2>
                        <span className="modal-icon-badge">
                            <FontAwesomeIcon icon={faHandshake} />
                        </span>
                        Add New Service
                    </h2>
                    <button type="button"
                        className="close-button"
                        onClick={onClose}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="stock-form">
                    {errors.general && (
                        <div className="error-message">{errors.general}</div>
                    )}

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="service_type">Service Type *</label>
                            <select
                                id="service_type"
                                name="service_type"
                                value={formData.service_type}
                                onChange={handleChange}
                                className={`form-select ${errors.service_type ? 'error' : ''}`}
                                disabled={loading}
                            >
                                <option value="">Select a service type...</option>
                                {serviceTypes.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                            {errors.service_type && (
                                <span className="error-text">{errors.service_type}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description *</label>
                        <input
                            type="text"
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            maxLength={500}
                            className={`form-input ${errors.description ? 'error' : ''}`}
                            placeholder="e.g. Ocean freight - Far East to Europe, Pre-shipment inspection..."
                            disabled={loading}
                        />
                        {errors.description && (
                            <span className="error-text">{errors.description}</span>
                        )}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="gl_account">GL Account</label>
                            <SearchableSelect
                                id="gl_account"
                                fetchUrl="/api/invoicing/gl-accounts/"
                                displayFn={(gl) => {
                                    const base = `${gl.account_number} - ${gl.account_name}`;
                                    return gl.entity_name ? `${base} (${gl.entity_name})` : base;
                                }}
                                value={formData.gl_account}
                                onChange={handleAccountingChange('gl_account')}
                                placeholder="Search GL account (optional)..."
                                disabled={loading}
                            />
                            <small style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>Default GL account suggestion. Operators pick the entity-correct GL at invoice time.</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="cost_category">Cost Category</label>
                            <SearchableSelect
                                id="cost_category"
                                fetchUrl="/api/invoicing/cost-categories/"
                                displayFn={(cc) => `${cc.code} - ${cc.short_name}`}
                                value={formData.cost_category}
                                onChange={handleAccountingChange('cost_category')}
                                placeholder="Search cost category (optional)..."
                                disabled={loading}
                            />
                            <small style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>Default cost category for reporting</small>
                        </div>
                    </div>

                    <div className="help-text">
                        <p>* Required fields</p>
                        <p>Enter quantity, unit price and VAT on the invoice line once the service is added.</p>
                    </div>

                    <div className="modal-actions centered">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    Add Service
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ServiceQuickForm;
